import InputAdornment from "@mui/material/InputAdornment";
import { useFormik } from "formik";
import { Plus, SendHorizontal, Trash2 } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { useChatbotContext } from "src/components/GlobalChatbot/ChatbotContext";
import { ChatbotLimitBar } from "src/components/GlobalChatbot/ChatbotLimitBar";
import type { ChatbotLimits } from "src/components/GlobalChatbot/types";
import * as Yup from "yup";

interface ChatbotComposerProps {
	onSend: (text: string) => Promise<void>;
	isLoading: boolean;
	limits?: ChatbotLimits;
}

const MAX_MESSAGE_LENGTH = 2000;
const DRAFT_STORAGE_KEY = "chatbot-draft-message";

export const ChatbotComposer = ({
	onSend,
	isLoading,
	limits,
}: ChatbotComposerProps) => {
	const { t } = useTranslation();
	const {
		attachableQuestion,
		attachedQuestion,
		attachQuestion,
		removeAttachedQuestion,
	} = useChatbotContext();

	const limitReached = limits?.left === 0;

	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: { message: localStorage.getItem(DRAFT_STORAGE_KEY) ?? "" },
		validationSchema: Yup.object().shape({
			message: Yup.string().trim().max(MAX_MESSAGE_LENGTH).required(),
		}),
		onSubmit: async ({ message }, { resetForm }) => {
			if (limitReached) return;
			resetForm();
			localStorage.removeItem(DRAFT_STORAGE_KEY);
			await onSend(message);
		},
	});

	return (
		<div className="chatbot-composer">
			{attachedQuestion ? (
				<div className="chatbot-attached-question">
					<div className="chatbot-attached-question-body">
						<div className="chatbot-attached-question-label">
							{t("globalChatbot.attachedQuestion.label", "Pytanie ze strony")}
						</div>
						<div className="chatbot-attached-question-preview">
							{attachedQuestion.preview}
						</div>
					</div>
					<IconButton
						data-test-id="global-chatbot-remove-attached-question"
						className="chatbot-attached-question-remove"
						onClick={removeAttachedQuestion}
					>
						<Trash2 className="icon-super-small" />
					</IconButton>
				</div>
			) : (
				attachableQuestion && (
					<button
						type="button"
						data-test-id="global-chatbot-attach-question-button"
						className="chatbot-attach-question-pill"
						onClick={attachQuestion}
					>
						<Plus className="icon-super-small" />
						{t("globalChatbot.attachQuestion", "Dołącz pytanie")}
					</button>
				)
			)}
			<TextFieldGrey
				data-test-id="global-chatbot-message-input"
				placeholder={t(
					"globalChatbot.composerPlaceholder",
					"Napisz swoje pytanie…",
				)}
				fullWidth
				multiline
				minRows={1}
				maxRows={4}
				value={values.message}
				disabled={isLoading || limitReached}
				onChange={(e) => {
					setFieldValue("message", e.target.value);
					localStorage.setItem(DRAFT_STORAGE_KEY, e.target.value);
				}}
				onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
					if (e.key === "Enter" && !e.shiftKey) {
						e.preventDefault();
						handleSubmit();
					}
				}}
				slotProps={{
					input: {
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									data-test-id="global-chatbot-send-button"
									className="chatbot-send-button"
									onClick={() => handleSubmit()}
									disabled={!values.message.trim() || limitReached}
									loading={isLoading}
								>
									<SendHorizontal className="icon-normal icon-primary" />
								</IconButton>
							</InputAdornment>
						),
					},
				}}
			/>
			{limits && <ChatbotLimitBar limits={limits} />}
		</div>
	);
};
